import { toast } from 'react-toastify';
import {
    validateBasicFieldsOthers,
    validateFAQs,
    validateImages,
    validateItinerary,
    validatePricesOthers,
    validateQuickView,
    validateTags,
    validateTextBlocks,
    validateTimeSlots
} from '../../../../utils/packUploads/uploadValidators';
import { normalizePriceFields, splitTextBlocks } from '../../../../utils/packUploads/transformers';
import { uploadImages, uploadVideo } from '../../../../utils/packUploads/mediaUploads';
import errorHandle from '../../../../utils/errorHandle';
import { supTripCreateFun, supTripEdit } from '../../../../api/supportAdmin';

const check = (result, ref, scrollArg) => {
    if (!result.valid) {
        toast.error(result.message);
        ref?.current?.scrollToAndHighlightField(result.field ?? scrollArg ?? '');
        return false;
    }
    return true;
};

export const supOrgActUpSubmit = async (
    activityData,
    tempData,
    imgData,
    setLoading,
    isEdit,
    videoFile,
    orgId,
    serviceType,
    refs = {}
) => {
    if (!check(validateBasicFieldsOthers(activityData), refs.basicRef)) return;
    if (!check(validateQuickView(activityData.quickView), refs.quickViewRef)) return;
    if (!check(validateTimeSlots(activityData), refs.timeSlotRef)) return;
    if (!check(validateItinerary(activityData), refs.itineraryRef)) return;
    if (!check(validateImages(imgData), refs.imageRef)) return;
    if (!check(validateTextBlocks(tempData), getTextRef(tempData, refs))) return;

    // Normalize price arrays before price validation
    activityData = { ...activityData, price: normalizePriceFields(activityData) };

    if (!check(validateFAQs(activityData.FAQ), refs.faqRef)) return;
    if (!check(validatePricesOthers(activityData), refs.priceRef, 'price')) return;
    //if (!check(validateInstallments(activityData.price))) return;
    if (!check(validateTags(activityData.tags), refs.tagsRef)) return;

    // ── 2. Build submission payload ──────────────────────────────────────────

    setLoading(true);

    try {
        const type = localStorage.getItem('type');
        const { images, cardImages } = await uploadImages(imgData, type, isEdit);
        const videos = await uploadVideo(videoFile, activityData, type, isEdit);

        const data = {
            ...activityData,
            ...splitTextBlocks(tempData),
            // organizer from this page's URL; the backend fills in the name
            organizerId: orgId,
            images,
            cardImages,
            videos
        };

        // ── 3. API call ──────────────────────────────────────────────────────

        const response = await submitToAPI(data, isEdit, serviceType);

        if (response.status === 200) {
            toast.success(`Package ${isEdit ? 'updated' : 'created'} successfully`);
            return response.status;
        }
    } catch (error) {
        errorHandle(error);
    } finally {
        setLoading(false);
    }
};

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

/**
 * Routes to the correct API function depending on edit vs. create
 * and (for edits) active vs. inactive trip status.
 */
async function submitToAPI(data, isEdit, serviceType) {
    let response;
    if (isEdit) {
        response = await supTripEdit(serviceType, data);
        return response;
    } else {
        const { _id, ...rest } = data;
        response = await supTripCreateFun(serviceType, rest);
        return response;
    }
}

/**
 * Returns the correct ref for the first failing text block field
 * so the scroll-to logic works across different section refs.
 */
function getTextRef(tempData, refs) {
    const fieldRefMap = {
        included: refs.inExRef,
        excluded: refs.inExRef,
        safety: refs.safetyRef,
        carry: refs.thingsRef,
        terms: refs.termsRef
    };

    // Return a proxy that exposes the right ref for whichever field fails
    return {
        current: {
            scrollToAndHighlightField: (field) => {
                const ref = fieldRefMap[field];
                ref?.current?.scrollToAndHighlightField(field);
            }
        }
    };
}
